/**
 * Where a newly built msService's `term` comes from.
 *
 * In priority order:
 *
 *   1. the term STORED on its supplier-product item, when that is a number --
 *      the exception, kept for the few products whose term is fixed by the
 *      supplier;
 *   2. the DEFAULT term -- the service group's `defaultTerm`, which is itself
 *      seeded from the opportunity's `defaultTerm` when the group is created.
 *      This is the primary source;
 *   3. FALLBACK_TERM, for everything with no default at all: opportunities
 *      that predate `defaultTerm`, and the Order and Company silos, which have
 *      no `defaultTerm`.
 *
 * ⚠ A supplier-product item with no stored term carries `null` (entered as
 * "None"). It must never be copied onto an msService as-is: an msService with a
 * null term reads as DIFFERENT from every sibling in the "all the same term"
 * roll-ups -- the webapp's (KitItemServices.getTermFromKitItemServices) and the
 * API's service-group term views alike -- so every kit and service group it sits
 * in would show "Varies". This function only ever returns a number.
 *
 * ⚠ It resolves only rows that HAVE a supplier-product item. A row built with
 * none (NOT_AVAILABLE) keeps its `'--'`, which is the msService-level marker for
 * "no term applies" and is deliberately left alone.
 */

export const FALLBACK_TERM = 36

export function isNumericTerm(term: unknown): term is number {
  return typeof term === 'number' && Number.isFinite(term)
}

export function resolveMSServiceTerm({
  supplierProductItemTerm,
  defaultTerm,
}: {
  supplierProductItemTerm: unknown
  defaultTerm: unknown
}): number {
  if (isNumericTerm(supplierProductItemTerm)) {
    return supplierProductItemTerm
  }
  if (isNumericTerm(defaultTerm)) {
    return defaultTerm
  }
  return FALLBACK_TERM
}

/**
 * The default for an option switched on inside an EXISTING kit.
 *
 * The kit's displayed term is the roll-up of its items (a single number, or
 * 'Varies'), so a kit the user re-termed with Edit Kit hands that term on to the
 * new option. 'Varies' -- or no term at all -- is not a term, so it falls
 * through to the service group's default.
 */
export function resolveDefaultTermForKitOption({
  kitTerm,
  masterServiceDefaultTerm,
}: {
  kitTerm: unknown
  masterServiceDefaultTerm: unknown
}): unknown {
  return isNumericTerm(kitTerm) ? kitTerm : masterServiceDefaultTerm
}
